/**
 * Header search — the global palette (⌘K / Ctrl+K) over the backend AND the app.
 *
 * The field is a button styled like an input; the surface behind it searches
 * transactions, addresses, catalog (entities/tags/labels/groups) and tenancy
 * (users/tenants/orgs) server-side through the shared `search-mgmt-api` provider
 * (`useSearchProvider`), with enrichment (chains, risk, labels) rendered via
 * `@authe/search`'s own `SearchResults`. Recents are scoped to the caller's
 * identity.
 *
 * ## Why it now navigates
 *
 * It used to search DATA only, on the reasoning that "page navigation lives in
 * the sidebar". That was true and it was the problem: the sidebar was the ONLY
 * route to a page, so every destination had to hold a permanent rail slot or
 * become unreachable, and a rail that holds everything teaches nothing. With a
 * **Pages** group here, folding the low-traffic sections of the rail costs
 * nobody their route to a page, and a reader who knows the name of what they
 * want never has to remember which section it was filed under.
 *
 * The group is built from the SAME `NAV_ITEMS` table the sidebar renders and
 * filtered through the same `visibleNavItems` gate, so the two cannot drift and
 * the palette cannot offer a page the session's RBAC would refuse. It matches on
 * label AND on section name, which is what makes the new grouping answerable
 * from the keyboard: "reference" lists the whole Reference section. Getting
 * Started — off the rail entirely — is in the table for exactly this reason.
 *
 * ## Why the palette shell is composed here
 *
 * `@authe/search`'s `SearchCommand` renders one `CommandGroup` per resource
 * KIND, ordered by the shared resource vocabulary, and takes no slot for a
 * group the host owns. Pages are not a backend resource kind and their rank is a
 * host decision (below an exact identifier, above a fuzzy data match — see
 * {@link orderedPaletteSections}), so this file composes the same pieces the
 * package's palette does — `useSearch`, `useRecentSearches`, `SearchResults`,
 * `FacetFilters` — inside its own `Dialog` instead. Everything with a decision
 * in it stays in the package or in `@/config`; what is here is ordering and
 * layout.
 */
import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { ShortcutManager } from "@authe/fe-commons/shortcuts";
import { toStoredChainId } from "@authe/fe-commons/chains";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Skeleton,
} from "@authe/ui-core";
import { usePermissions } from "@authe/ui-core/permissions";
import { useEntitled } from "@authe/ui-core";
import {
  FacetFilters,
  hitAddress,
  hitChains,
  hitTxHash,
  SearchResults,
  SEARCH_MIN_QUERY_LENGTH,
  useRecentSearches,
  useSearch,
  type SearchGroup,
  type SearchHit,
  type SearchResourceKind,
} from "@authe/search";
import { useSearchProvider } from "@/api/adapters";
import { useStorageIdentity } from "@/app/storageIdentity";
import {
  ROUTES,
  isShortNavQuery,
  matchNavPages,
  visibleNavItems,
  type NavItem,
  type NavPageMatch,
} from "@/config";

/**
 * Build the `/transactions/$id` route param for a transaction hit.
 *
 * The search backend emits one hit per chain with a route-ready id: a
 * content-addressed `canonical_tx_id` (bare 64-hex) for indexed txs, or the
 * chain-scoped `<chain>:<hash>` form for not-yet-indexed ones. Both route as-is
 * — a value already carrying a `:` is chain-scoped, and a bare-hex id is
 * canonical. Only a bare raw hash (`0x…`, e.g. from a custom provider) is
 * ambiguous across forked chains, so we prefix it with the hit's chain.
 *
 * The prefix is the canonical CAIP-2 id, which itself contains a colon
 * (`eip155:1:0xabc…`). That is unambiguous to the API, which splits the ref at
 * its LAST colon — the hash never contains one.
 */
function transactionRouteId(hit: SearchHit): string {
  if (hit.id.includes(":") || !hit.id.toLowerCase().startsWith("0x")) return hit.id;
  const chainId = hit.chainId ?? hit.enrichment?.chains?.[0] ?? null;
  const chainToken = toStoredChainId(chainId) ?? chainId;
  return chainToken ? `${chainToken}:${hit.id}` : hit.id;
}

/**
 * The `?chain=` hint for an address hit, or `null` for a cross-chain one.
 *
 * A pasted address resolves to ONE hit covering every chain it is active on,
 * carrying a `chainId` the backend picked only to read catalog overlays with
 * (the first chain of the activity rollup) — it is not a chain the caller asked
 * for. `/addresses/$id` reads `?chain=` as a deliberate deep link that outranks
 * the address's own primary chain, so a multi-chain hit must navigate bare and
 * let that page's cross-chain header + chain switcher do the disambiguation.
 *
 * Picking a chain from the palette's drilldown narrows the hit to that single
 * chain, which is precisely what makes the hint deliberate here.
 */
function addressChainHint(hit: SearchHit): string | null {
  const chains = hitChains(hit);
  return chains.length === 1 ? chains[0] ?? null : null;
}

/**
 * Did the caller type this record's identifier, rather than something like it?
 *
 * An exact identifier is not a search, it is a lookup — the reader has the hash
 * or the address in their clipboard and wants that one row. Those hits stay
 * above everything, Pages included.
 */
function isExactIdHit(hit: SearchHit, query: string): boolean {
  const text = query.trim().toLowerCase();
  if (!text) return false;
  return [hit.id, hitAddress(hit), hitTxHash(hit)].some(
    (value) => typeof value === "string" && value.toLowerCase() === text,
  );
}

/** One group's hits, minus the ones that fail `keep`. Empty groups drop out. */
function filterGroups(groups: SearchGroup[], keep: (hit: SearchHit) => boolean): SearchGroup[] {
  return groups
    .map((group) => ({ ...group, hits: group.hits.filter(keep) }))
    .filter((group) => group.hits.length > 0);
}

/** The palette's body, in render order. */
interface PaletteSections {
  /** Data groups whose hits the caller typed verbatim. Always first. */
  exact: SearchGroup[];
  /** Data groups matched by relevance rather than identity. */
  fuzzy: SearchGroup[];
  /** Nav entries matching the query, already gated and ranked. */
  pages: NavPageMatch[];
  /** Whether Pages renders before or after {@link fuzzy}. */
  pagesBeforeFuzzy: boolean;
}

/**
 * Rank the three kinds of answer against each other.
 *
 * An exact identifier outranks everything — the caller pasted a hash, they want
 * the record. Below that the query's SHAPE decides: a short query is a word, and
 * a word is far more often the name of a page than a fragment of some address,
 * so Pages go above the fuzzy data matches. A long query is an identifier that
 * did not resolve exactly, where a page whose label happens to share a substring
 * is noise in front of the near-miss records — so Pages go last.
 */
export function orderedPaletteSections(
  groups: SearchGroup[],
  pages: NavPageMatch[],
  query: string,
): PaletteSections {
  return {
    exact: filterGroups(groups, (hit) => isExactIdHit(hit, query)),
    fuzzy: filterGroups(groups, (hit) => !isExactIdHit(hit, query)),
    pages,
    pagesBeforeFuzzy: isShortNavQuery(query),
  };
}

export function HeaderSearch(): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const provider = useSearchProvider();
  const { can } = usePermissions();
  // The packaging half of the same filter: `can` answers "may this person",
  // `entitled` answers "does this customer's plan include it".
  const entitled = useEntitled();

  // The one storage identity (`@/app/storageIdentity`): the full
  // customer/org/tenant/user quadruple, so this surface's keyspace is the
  // same one every other surface writes under.
  const identity = useStorageIdentity();

  const search = useSearch({ provider });
  const recent = useRecentSearches({ identity });

  // Owns ⌘K (mod = ⌘ on mac, ctrl elsewhere) — the dialog is controlled and
  // registers no shortcut of its own.
  React.useEffect(() => {
    const manager = new ShortcutManager();
    manager.register({
      combo: "mod+k",
      description: "Open search",
      allowInInput: true,
      handler: () => setOpen((v) => !v),
    });
    return manager.attach();
  }, []);

  const { clear } = search;
  React.useEffect(() => {
    if (!open) clear();
  }, [open, clear]);

  const onSelectHit = React.useCallback(
    (hit: SearchHit) => {
      recent.add(search.text);
      setOpen(false);
      switch (hit.kind) {
        case "transaction":
          navigate({ to: "/transactions/$id", params: { id: transactionRouteId(hit) } });
          break;
        case "address": {
          const chain = addressChainHint(hit);
          navigate({
            to: "/addresses/$id",
            params: { id: hit.id },
            search: chain ? { chain } : {},
          });
          break;
        }
        case "entity":
          navigate({ to: "/entities/$id", params: { id: hit.id } });
          break;
        case "group":
          navigate({ to: "/groups/$id", params: { id: hit.id } });
          break;
        case "label":
          navigate({ to: "/labels/$id", params: { id: hit.id } });
          break;
        case "tag":
          navigate({ to: "/tags/$id", params: { id: hit.id } });
          break;
        case "user":
          navigate({ to: "/team" });
          break;
        case "organization":
        case "tenant":
          // The page that LISTS them. `/settings` used to hold every settings
          // card at once, so landing there happened to work; it is now the
          // appearance page, which answers nothing about a tenant.
          navigate({ to: ROUTES.settingsWorkspace });
          break;
        default:
          break;
      }
    },
    [navigate, recent, search.text],
  );

  const onSelectPage = React.useCallback(
    (item: NavItem) => {
      recent.add(search.text);
      setOpen(false);
      navigate({ to: item.to });
    },
    [navigate, recent, search.text],
  );

  // Gated exactly as the sidebar gates it, through the same helper — the palette
  // must never be a way around a permission the rail respects, nor around a
  // feature the customer's plan does not include.
  const pages = React.useMemo(
    () => matchNavPages(search.text, visibleNavItems(can, entitled)),
    [search.text, can, entitled],
  );

  // Counts are meaningless once the caller has narrowed to a kind (the facet
  // then only counts what survived its own filter), so they drop out there.
  const kindCounts = React.useMemo(() => {
    if (search.kinds.length > 0) return undefined;
    const facet = search.facets.find((f) => f.field === "kind");
    if (!facet) return undefined;
    const out: Partial<Record<SearchResourceKind, number>> = {};
    for (const v of facet.values) {
      if (v.count != null) out[v.value as SearchResourceKind] = v.count;
    }
    return out;
  }, [search.facets, search.kinds.length]);

  const showRecents = search.text.trim().length < SEARCH_MIN_QUERY_LENGTH;
  const sections = React.useMemo(
    () => orderedPaletteSections(search.groups, pages, search.text),
    [search.groups, pages, search.text],
  );
  const hidden = search.total != null ? search.total - search.hits.length : 0;
  const nothingFound =
    !showRecents &&
    search.status === "ready" &&
    search.groups.length === 0 &&
    sections.pages.length === 0;

  const pagesGroup =
    sections.pages.length > 0 ? (
      <CommandGroup heading="Pages">
        {sections.pages.map(({ item }) => {
          const Icon = item.icon;
          return (
            <CommandItem
              key={item.to}
              value={`page:${item.to}`}
              onSelect={() => onSelectPage(item)}
            >
              <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate">{item.label}</span>
              {/* The section the entry lives in, so selecting it also TEACHES
                  where the rail keeps it. `Getting Started` carries none. */}
              {item.section && (
                <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">
                  {item.section}
                </span>
              )}
            </CommandItem>
          );
        })}
      </CommandGroup>
    ) : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-9 w-full max-w-md items-center gap-2 rounded-md border border-border bg-muted/40 px-3 text-sm text-muted-foreground transition-colors hover:bg-muted"
      >
        <Search className="h-4 w-4" />
        <span className="flex-1 text-left">Search…</span>
        <kbd className="hidden rounded border border-border bg-background px-1.5 text-[10px] font-medium sm:inline">
          ⌘K
        </kbd>
      </button>
      {/* `Dialog` + our own `Command` rather than ui-core's `CommandDialog`,
          which owns its `Command` root and so cannot be told `shouldFilter`. */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl overflow-hidden p-0" showClose={false}>
          <DialogTitle className="sr-only">Search</DialogTitle>
          <DialogDescription className="sr-only">
            Search addresses, transactions, entities — or jump to a page.
          </DialogDescription>
          {/* `shouldFilter={false}`: data matching happens server-side in the
              provider and page matching in `matchNavPages`, so cmdk must render
              exactly what those two decided rather than filter it again. */}
          <Command shouldFilter={false} className="[&_[cmdk-group-heading]]:px-2">
            <CommandInput
              value={search.text}
              onValueChange={search.setText}
              placeholder="Search addresses, transactions, entities, pages…"
            />
            <FacetFilters
              selected={search.kinds}
              onToggle={search.toggleKind}
              counts={kindCounts}
            />
            <CommandList className="max-h-[60vh]">
              {search.status === "searching" && (
                <div className="space-y-2 p-3">
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-6 w-4/5" />
                  <Skeleton className="h-6 w-3/5" />
                </div>
              )}
              {search.status === "error" && (
                <div className="p-4 text-center text-sm text-destructive">
                  Search failed. Try again.
                </div>
              )}
              {showRecents && search.status !== "searching" && recent.recents.length === 0 && (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  Type to search across addresses, transactions, entities — or jump to a page.
                </div>
              )}
              {showRecents && recent.recents.length > 0 && (
                <CommandGroup heading="Recent">
                  {recent.recents.map((r) => (
                    <CommandItem
                      key={r.text}
                      value={`recent:${r.text}`}
                      onSelect={() => search.setText(r.text)}
                    >
                      <span className="truncate">{r.text}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {nothingFound && <CommandEmpty>No matches.</CommandEmpty>}
              {!showRecents && (
                <>
                  {sections.exact.length > 0 && (
                    <SearchResults
                      groups={sections.exact}
                      onSelect={onSelectHit}
                      chainDrilldown
                      scopeDrilldown
                    />
                  )}
                  {sections.pagesBeforeFuzzy && pagesGroup}
                  {sections.fuzzy.length > 0 && (
                    <SearchResults
                      groups={sections.fuzzy}
                      onSelect={onSelectHit}
                      chainDrilldown
                      scopeDrilldown
                    />
                  )}
                  {!sections.pagesBeforeFuzzy && pagesGroup}
                  {hidden > 0 && (
                    <>
                      <CommandSeparator />
                      <div className="px-3 py-2 text-center text-xs text-muted-foreground">
                        Showing {search.hits.length} of {search.total} matches — narrow the query
                        or pick a type.
                      </div>
                    </>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
}
